/**
 * The site's component recipes, ported from `components/` and pruned to what
 * a call site can actually select.
 *
 * The pruning was counted, not guessed: every `variant="..."` and `size="..."`
 * in the remaining tsx files was tallied, and the dynamic cases were checked by
 * hand. One of them matters: `Callouts.tsx:16` renders `<Alert variant={type}>`
 * where `type` defaults to 'default', so all four Alert variants are live even
 * though only `info` ever appears as a literal.
 *
 * Dropped entirely, each with zero consumers: the Avatar, Checkbox, Switch,
 * Input, InputGroup and Textarea recipes (no `variant="rounded"`, `"privacy"`,
 * `"brand"`, `"square"` or `"ghost"` on any of them exists in the tree), the
 * Menu recipe (SearchResultItem imports `MenuItemProps` as a type and no Menu
 * is ever rendered), Link's `hover-opacity` and `right-bottom-nav`, and nine of
 * the twelve Button variants: invisible, ghost-hover, ghost-hover-opacity,
 * outline-hover-filled, ghost-hover-outline, pq-outline, pq-solid, pq-ghost,
 * filledGreen, filledYellow and filledRed.
 *
 * Token paths are untouched. v3 resolves tokens by path, so every
 * `components.button.solid.bgColor` string here means what it meant in v2.
 */
import { defineRecipe, defineSlotRecipe } from '@chakra-ui/react';

/**
 * The six sizes the docs headings select, and the reason they are sizes at all.
 *
 * `main-content/heading` sized its headings with a flat `fontSize` STYLE PROP,
 * one value per level. That worked in v2 for two reasons that both stop being
 * true in v3.
 *
 * The values were bare numeric strings ('36', '30', ...). v2's styled-system
 * coerced a unitless number to pixels; v3 passes an unresolvable fontSize token
 * through verbatim, and `font-size: 36` is invalid CSS that the browser drops
 * at parse time. Hence the `px`.
 *
 * And v2 merged the styleConfig and the style props into one object before
 * serialising, with `assignAfter`, so a scalar prop deleted and replaced the
 * styleConfig's whole responsive `fontSize` array. v3 runs the recipe through
 * `css()` FIRST, which turns `fontSize: ['3xl', null, '4xl']` into a flat
 * fontSize plus an `@media` key, and only then merges the style props. The prop
 * overwrites the unconditional entry and the recipe's media rule wins again
 * from 768px up. Naming the breakpoint in the prop does not help: `css()` walks
 * the merged object in key order, so the prop writes into the media bucket and
 * the recipe overwrites it a moment later.
 *
 * A recipe variant has no such problem. Variants merge as raw style objects
 * BEFORE the single `css()` call, so a flat `fontSize` here replaces the
 * responsive one from `defaultVariants.size` and no media entry for font-size
 * is emitted at all. A caller's `fontSize` prop can then override it again,
 * because there is no longer a media rule for it to lose to.
 *
 * `lineHeight` stays responsive, because v2's did: the docs Heading never
 * overrode it, so it kept size xl's `[1.33, null, 1.2]`.
 */
const docsHeadingSizes = Object.fromEntries(
  Object.entries({ h1: 36, h2: 30, h3: 24, h4: 20, h5: 18, h6: 16 }).map(
    ([level, px]) => [
      level,
      // A fresh array per level. `mergeWith` assigns array references straight
      // through and then mutates them in place on the next merge, so a shared
      // literal would be a live wire between the six entries.
      { fontSize: `${px}px`, lineHeight: [1.33, null, 1.2] }
    ]
  )
);

/**
 * One of two recipes the site did not have and now needs. The other is
 * `containerRecipe`. Both restore a v2 built-in default the site never
 * overrode and therefore never wrote down.
 *
 * v2 mapped `<Heading size="md">` to fontSize xl (1.25rem); v3 maps it to
 * textStyle md, which is 1rem. Four headings pass size="md" (TableOfContent
 * and three in the qasm playground) and would quietly shrink by a fifth. The
 * scale below is v2's own, read off `theme.components.Heading.sizes`.
 *
 * `baseStyle.color: brand.900` comes from `components/heading.ts` and is kept
 * as it was, but it is worth knowing what it does: brand.900 is #18011a here,
 * so every heading is near-black in dark mode too.
 */
export const headingRecipe = defineRecipe({
  base: { fontFamily: 'heading', fontWeight: 'bold' },
  variants: {
    size: {
      '4xl': { fontSize: ['6xl', null, '7xl'], lineHeight: 1 },
      '3xl': { fontSize: ['5xl', null, '6xl'], lineHeight: 1 },
      '2xl': { fontSize: ['4xl', null, '5xl'], lineHeight: [1.2, null, 1] },
      xl: { fontSize: ['3xl', null, '4xl'], lineHeight: [1.33, null, 1.2] },
      lg: { fontSize: ['2xl', null, '3xl'], lineHeight: [1.33, null, 1.2] },
      md: { fontSize: 'xl', lineHeight: 1.2 },
      sm: { fontSize: 'md', lineHeight: 1.2 },
      xs: { fontSize: 'sm', lineHeight: 1.2 },
      /**
       * SearchResultSectionTitle, the group label inside the search overlay.
       * v2 set it with a flat `fontSize="12px"` prop, which in v3 loses to the
       * default size's media rule from 768px up and rendered the twelve-pixel
       * label at thirty-six. Same mechanism as docsHeadingSizes, same remedy.
       */
      'menu-group': { fontSize: '12px', lineHeight: [1.33, null, 1.2] },
      ...docsHeadingSizes
    }
  },
  defaultVariants: { size: 'xl' }
});

/**
 * v2's Container defaults, which the site never declared and therefore never
 * noticed it depended on. v3 ships a Container with no maxW, and fifteen
 * `<Container>` call sites (several with an explicit maxW="7xl" or "3xl",
 * several without) would have gone full-bleed.
 */
export const containerRecipe = defineRecipe({
  base: {
    w: '100%',
    marginX: 'auto',
    maxW: '60ch',
    px: '4'
  }
});

/**
 * Three of twelve. `solid` and `outline` are v3 built-ins being overridden;
 * `ghost` too. `link` is not: v2 had a Button `link` variant and v3 dropped it,
 * and `qasm-playground.tsx` selects it twice through `<Link as={Button}>`,
 * where the rendered component is the Button and its recipe is what resolves.
 *
 * `_focus` is carried over from v2 verbatim. It is the same declaration as
 * `_hover` in every variant that has one, which is what v2 did.
 */
export const buttonRecipe = defineRecipe({
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    // v2's Button put a gap between icon and label through its own base. v3
    // declares gap per SIZE, and a base-level gap never reaches the DOM once a
    // size is selected, so it is spelled out on each size below instead.
    fontWeight: 'semibold',
    lineHeight: '1.2',
    borderRadius: 'full',
    transitionProperty: 'common',
    transitionDuration: 'normal',
    _disabled: { opacity: 0.4, cursor: 'not-allowed', boxShadow: 'none' }
  },
  variants: {
    variant: {
      solid: {
        px: 5,
        bgColor: 'components.button.solid.bgColor',
        color: 'components.button.solid.color',
        _hover: {
          bgColor: 'components.button.solid.hover.bgColor',
          opacity: 1
        },
        _focus: {
          bgColor: 'components.button.solid.hover.bgColor',
          opacity: 1
        },
        _active: { bgColor: 'components.button.solid.active.bgColor' },
        _disabled: {
          bgColor: 'components.button.solid.disabled.bgColor',
          color: 'components.button.solid.disabled.color'
        }
      },
      outline: {
        px: 5,
        textTransform: 'capitalize',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'components.button.outline.borderColor',
        color: 'components.button.outline.color',
        _hover: {
          borderColor: 'components.button.outline.hover.borderColor',
          bgColor: 'components.button.outline.hover.bgColor',
          color: 'components.button.outline.hover.color',
          opacity: 1
        },
        _focus: {
          bgColor: 'components.button.outline.hover.borderColor',
          opacity: 1
        }
      },
      ghost: {
        px: 5,
        textTransform: 'capitalize',
        bgColor: 'transparent',
        opacity: 0.7,
        _hover: {
          bgColor: 'components.button.ghost.hover.bgColor',
          opacity: 1
        },
        _focus: {
          bgColor: 'components.button.ghost.hover.bgColor',
          opacity: 1
        }
      },
      // v2's built-in. Not a button shape at all: no box, no padding, no
      // height, and it underlines on hover.
      link: {
        padding: 0,
        height: 'auto',
        lineHeight: 'normal',
        verticalAlign: 'baseline',
        borderRadius: 0,
        color: 'colorPalette.fg',
        _hover: { textDecoration: 'underline' },
        _active: { color: 'colorPalette.emphasized' }
      }
    },
    size: {
      xs: { h: 6, minW: 6, fontSize: 'xs', px: 2, gap: 1 },
      sm: { h: 8, minW: 8, fontSize: 'sm', px: 3, gap: 2 },
      md: { h: 10, minW: 10, fontSize: 'md', px: 4, gap: 2 },
      lg: { h: 12, minW: 12, fontSize: 'lg', px: 6, gap: 3 }
    }
  },
  defaultVariants: { variant: 'solid', size: 'md' }
});

/**
 * Two of four. `hover-theme` is selected once (the CMS contact page) and
 * `pq-footer` once (the site footer). The name is fork residue and is kept so
 * the call site does not have to move.
 */
export const linkRecipe = defineRecipe({
  /**
   * `color: inherit` on both default variants is the whole point of this
   * recipe. v3's built-in Link paints itself `colorPalette.fg`, and this site
   * sets `html { colorPalette: 'brand' }`, so every link in the tree came out
   * violet — including all thirty entries of the docs sidebar, which v2 drew
   * in grey. Inheriting puts them back under whatever colours the surface,
   * which is what the nav's own active/inactive props expect to control.
   *
   * The base is v2's too: v3 made Link an inline-FLEX box with a gap, which
   * changes how a link sits inside a line of text.
   */
  base: { display: 'inline', gap: 0, alignItems: 'normal' },
  variants: {
    variant: {
      plain: { color: 'inherit' },
      underline: { color: 'inherit', _hover: { textDecoration: 'underline' } },
      'hover-theme': {
        color: 'inherit',
        _hover: { color: 'components.link._hover.color' },
        transition: 'color 0.1s ease-in-out'
      },
      'pq-footer': {
        color: 'inherit',
        fontWeight: 500,
        opacity: 0.6,
        _hover: { opacity: 1 },
        transition: 'opacity 0.2s ease-in-out'
      }
    }
  },
  defaultVariants: { variant: 'plain' }
});

/**
 * v2's Divider. v3 renamed the component to Separator and its recipe with it,
 * and the ten `<Divider>` call sites keep working only because the sweep
 * renames them; the recipe has to exist either way or they lose their line.
 */
export const separatorRecipe = defineRecipe({
  base: { borderColor: 'border', borderStyle: 'solid' },
  variants: {
    orientation: {
      horizontal: { borderTopWidth: '1px', width: '100%' },
      vertical: { borderInlineStartWidth: '1px', height: '100%' }
    }
  },
  defaultVariants: { orientation: 'horizontal' }
});

/** v2's Skeleton, whose only override was the corner radius. */
export const skeletonRecipe = defineRecipe({
  base: {
    borderRadius: 'md',
    animation: 'pulse',
    animationDuration: '1.2s',
    background: 'bg.emphasized'
  }
});

/** The keycaps in the search button. v2's Kbd, which the site never overrode. */
export const kbdRecipe = defineRecipe({
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    fontFamily: 'mono',
    fontWeight: 'bold',
    lineHeight: 'normal',
    borderRadius: 'md',
    borderWidth: '1px',
    borderBottomWidth: '3px',
    px: '1',
    fontSize: 'sm',
    bg: 'bg.subtle',
    color: 'fg'
  }
});

/**
 * The docs callouts. All four variants are live through
 * `<Alert variant={type}>`, and `type` is a plain string prop with 'default'
 * as its fallback, so a literal grep would have found only `info`.
 *
 * v2 wrote `border: '1px solid'` per variant. v3's preflight sets
 * `border-style: solid` on `*` but loses to the shorthand, so the two halves
 * are declared separately here.
 */
const calloutVariant = (name: string) => ({
  root: {
    borderWidth: '1px',
    borderStyle: 'solid',
    bgColor: `components.callout.${name}.container.bgColor`,
    borderColor: `components.callout.${name}.container.borderColor`,
    color: `components.callout.${name}.container.color`
  }
});

export const alertSlotRecipe = defineSlotRecipe({
  slots: ['root', 'title', 'description', 'indicator', 'content'],
  base: {
    root: {
      display: 'flex',
      alignItems: 'flex-start',
      width: '100%',
      position: 'relative'
    },
    content: { display: 'flex', flexDirection: 'column', gap: '1', flex: '1' },
    title: { fontWeight: 'medium' },
    description: { display: 'inline' },
    indicator: { display: 'inline-flex', flexShrink: 0 }
  },
  variants: {
    variant: {
      default: calloutVariant('default'),
      info: calloutVariant('info'),
      warning: calloutVariant('warning'),
      error: calloutVariant('error'),
      // GoogleMaps selects this one, and it is a v3 built-in the site never
      // declared. Restated here because declaring `variants.variant` at all
      // replaces the built-in map rather than extending it.
      subtle: {
        root: { bg: 'colorPalette.subtle', color: 'colorPalette.fg' }
      }
    }
  },
  defaultVariants: { variant: 'default' }
});

/** The docs sidebar tree. v2's `leftNav` variant, slot names mapped to v3's. */
export const accordionSlotRecipe = defineSlotRecipe({
  slots: [
    'root',
    'item',
    'itemTrigger',
    'itemContent',
    'itemBody',
    'itemIndicator'
  ],
  variants: {
    variant: {
      leftNav: {
        root: { fontSize: 'sm' },
        item: { borderWidth: 0 },
        itemTrigger: {
          textAlign: 'left',
          fontSize: 'sm',
          lineHeight: 1.5,
          fontWeight: 'normal',
          px: 4,
          gap: 0
        },
        itemContent: {
          paddingTop: 0,
          paddingRight: 0,
          fontSize: 'sm',
          /**
           * v2's `reduceMotion`, and the 4px it was quietly worth.
           *
           * PageDirectory passed `reduceMotion` to v2's Accordion on purpose,
           * with a comment: a panel toggled while the nav is hidden measures an
           * open height of 0 and gets stuck expanded but invisible. v3 has no
           * such prop and drives the collapse from the recipe instead, so the
           * guard is reinstated here by taking the animation away.
           *
           * `overflow` is the half that shows up in a screenshot. v3 pairs the
           * animation with `overflow: hidden`, which makes itemContent a block
           * formatting context, and a BFC traps the margins of the items inside
           * it. Every nested entry carries `my={1}`, so the first one's 4px and
           * the last one's 4px stopped collapsing out of the panel the way they
           * did through v2's `overflow: visible` one, and each expanded section
           * pushed the rest of the nav 4px down. With no animation left to
           * clip, visible is both correct and free.
           */
          overflow: 'visible',
          _open: { animationName: 'none' },
          _closed: { animationName: 'none' }
        },
        itemBody: { paddingTop: 0, paddingRight: 0, paddingLeft: 4 },
        itemIndicator: { color: 'inherit', display: 'flex' }
      }
    }
  }
});

/** v2's Tooltip, restyled to the site's token. */
export const tooltipSlotRecipe = defineSlotRecipe({
  slots: ['content', 'arrow', 'arrowTip'],
  base: {
    content: {
      borderRadius: 'md',
      bgColor: 'components.tooltip.bgColor',
      color: 'shared.text.default',
      fontWeight: 'medium',
      px: '2',
      py: '1',
      fontSize: 'xs',
      maxWidth: '20rem',
      zIndex: 'tooltip'
    },
    arrow: { '--arrow-size': 'sizes.2', '--arrow-background': 'inherit' },
    arrowTip: { borderTopWidth: '1px', borderInlineStartWidth: '1px' }
  }
});

/** The docs breadcrumb. v2's built-in, which the site never overrode. */
export const breadcrumbSlotRecipe = defineSlotRecipe({
  slots: ['root', 'list', 'item', 'link', 'currentLink', 'separator'],
  base: {
    list: {
      display: 'flex',
      alignItems: 'center',
      wordBreak: 'break-word',
      gap: '1.5'
    },
    item: { display: 'inline-flex', alignItems: 'center' },
    link: {
      outline: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      cursor: 'pointer',
      textDecoration: 'none',
      _hover: { textDecoration: 'underline' }
    },
    currentLink: { display: 'inline-flex', alignItems: 'center' },
    separator: { display: 'inline-flex', alignItems: 'center', opacity: 0.8 }
  }
});

/** The MDX lists. v2's built-in. */
export const listSlotRecipe = defineSlotRecipe({
  slots: ['root', 'item', 'indicator'],
  /**
   * v2's UnorderedList and OrderedList were block elements with a 1em start
   * margin and no gap. v3's List.Root is a flex column with `gap: 1`, so every
   * MDX list lost its indent and gained a quarter-rem between items. This is
   * netsnek.com's recipe, and the two sites render MDX from the same editor.
   */
  base: {
    root: { display: 'block', marginInlineStart: '1em' }
  }
});
